<script setup lang="ts">
import { PropType } from 'vue'
import ProductGiftAction from '~/models/GiftEvent/ProductGiftAction'
import ImagePreview from '~/components/elements/ImagePreview.vue'
import { useBasketStore } from '~/stores/basket'

const props = defineProps({
    giftAction: {
        type: Object as PropType<ProductGiftAction>,
        default: () => ({}),
    },
})

const activeProducts = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.getGiftEventActiveProducts(props.giftAction.giftEventId).giftEventActiveProducts
})

const isGiftEventConditionMet = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.getGiftEventActiveProducts(props.giftAction.giftEventId).isConditionMet
})

const discount = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.getGiftEventActiveProducts(props.giftAction.giftEventId).discount
})

const url = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.getGiftEventActiveProducts(props.giftAction.giftEventId).url
})

const showDelimiter = (count: number, giftAction: ProductGiftAction, isDesktop: boolean) => {
    return (isDesktop || count % 2 !== 0) && count < (giftAction.percentageDiscountGiftMinAmount || 0)
}
</script>

<template>
    <div
        v-if="giftAction.percentageDiscountGiftMinAmount !== null && giftAction.percentageDiscountGiftMinAmount > 0"
        class="gift-action__wrapper"
    >
        <div class="gift-action__title">
            <img v-if="giftAction.icon" :src="giftAction.icon" alt="action" />
            <h2>{{ giftAction.title }}</h2>
        </div>

        <div class="gift-action__subtitle" v-html="giftAction.subtitle"></div>

        <div v-if="isGiftEventConditionMet" class="gift-action__condition_success" data-cy="gift-action-success">
            {{ $t('giftAction.conditionMet') }}
        </div>
        <div v-if="discount > 0" class="gift-action__content" data-cy="gift-action-info">
            <template v-for="(count, index) in giftAction.percentageDiscountGiftMinAmount" :key="count">
                <div class="gift-action__product">
                    <template v-if="activeProducts && activeProducts[index]">
                        <ImagePreview
                            :image-url="activeProducts[index].image"
                            :image-alt="activeProducts[index].name"
                            :lazy-image="false"
                            :out-of-stock-greyed-out="false"
                        />
                    </template>
                    <template v-else>
                        <a v-if="url" :href="url" class="gift-action__product_text_link">
                            <span
                                v-if="discount < 100 && index + 1 === giftAction.percentageDiscountGiftMinAmount"
                                v-html="$t('giftAction.addProductTextWithDiscount', { discount })"
                            >
                            </span>
                            <template v-else>
                                {{ $t('giftAction.addProductText') }}
                            </template>
                        </a>
                        <div v-else class="gift-action__product_text" data-cy="gift-action-text">
                            <span
                                v-if="discount < 100 && index + 1 === giftAction.percentageDiscountGiftMinAmount"
                                v-html="$t('giftAction.addProductTextWithDiscount', { discount })"
                            >
                            </span>
                            <template v-else>
                                {{ $t('giftAction.addProductText') }}
                            </template>
                        </div>
                    </template>
                </div>
                <div v-if="showDelimiter(count, giftAction, $device.isDesktop)" class="gift-action__plus-delimiter">
                    <img src="/nuxt-assets/icons/square-plus.svg" alt="plus" />
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.gift-action {
    &__wrapper {
        margin-bottom: 1.5rem;
    }

    &__title {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        color: $color-cta;

        img {
            max-width: 20px;
            max-height: 20px;
            margin-right: 0.5rem;
        }

        h2 {
            margin: 0;
        }
    }

    &__content {
        display: grid;
        grid-template-columns: 1fr 35px 1fr;
        gap: 1rem;
        margin: 1rem 0;

        @include screen-md {
            display: flex;
        }
    }

    &__product {
        position: relative;
        width: 135px;
        height: 135px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: $box-shadow;

        picture {
            padding: 0.5rem;
        }
    }

    &__product_text {
        text-align: center;
        color: $color-dark;
    }

    &__product_text_link {
        text-align: center;
        color: $color-dark;
        height: 100%;
        display: flex;
        align-items: center;

        &:hover {
            text-decoration: underline;
            background: #fafafa;
        }
    }

    &__plus-delimiter {
        display: flex;
        justify-content: center;
        align-items: center;

        img {
            width: 35px;
            height: 35px;
        }
    }

    &__condition_success {
        text-align: center;
        color: $color-green-dark;
        background-color: $color-green-light;
        border-radius: $border-radius;
        padding: 1.5rem 0;
        margin: 1rem 0;
    }
}
</style>
